import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Container, Link, Paper, Stack, Typography } from '@mui/material';
import { Edit as EditIcon, Place as PlaceIcon, Schedule as DeadlineIcon, Work as WorkIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import BackButton from 'components/common/buttons/BackButton';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import { ConfirmIconDelete } from 'components/collab/lab/LabDialogs';
import { ApplicantsDialog, ApplyDialog } from 'components/collab/lab/LabExtras';
import PositionDialog from './PositionDialog';
import { EMPLOYMENT, HOST_LABEL, KIND, WORK_MODE, daysLeft, fmtDeadline } from './positionLabels';

const hostLink = (h) => (h?.type === 'lab' ? `/lab/${h.id}/` : h?.type === 'consortium' ? `/consortium/${h.id}` : `/organization/${h?.id}`);

export default function PositionPage() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { showSnackbar } = useSnackbar();
  const { data: p, error, mutate } = useSWR(`/consortium/positions/${positionId}/`, fetcher);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applicantsOpen, setApplicantsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {                               // from the "someone applied" notification
    if (p?.can_manage && new URLSearchParams(search).get('applicants')) setApplicantsOpen(true);
  }, [p?.can_manage, search]);

  if (error) return <Container sx={{ py: 4 }}><Alert severity="info">{error.response?.status === 404 ? 'This position is no longer available.' : apiErrorMessage(error)}</Alert></Container>;
  if (!p) return <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  const left = daysLeft(p.deadline);
  const color = /^#[0-9a-f]{6}$/i.test(p.host?.color || '') ? p.host.color : '#0369a1';
  const remove = async () => {
    try { await axiosService.delete(`/consortium/positions/${p.id}/`); showSnackbar('Position removed', 'success'); navigate('/hub/', { state: { tab: 3 } }); }
    catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const recipient = p.host?.type === 'lab' ? `The lab's PI` : (p.posted_by_detail?.name || 'Whoever announced it');

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <BackButton position="inline" />
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, mt: 1, borderRadius: 3, borderTop: `4px solid ${color}` }}>
        <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
          <Box flex={1} minWidth={260}>
            <Chip size="small" icon={<WorkIcon />} label={KIND[p.kind] || p.kind} sx={{ bgcolor: `${color}18`, color, fontWeight: 600, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: { xs: '24px', md: '30px' } }}>{p.title}</Typography>
            <Typography sx={{ mt: 1 }} color="text.secondary">
              {HOST_LABEL[p.host?.type]}:{' '}
              <Link component={RouterLink} to={hostLink(p.host)} underline="hover" sx={{ fontWeight: 600 }}>{p.host?.name}</Link>
              {p.host?.type !== 'organization' && p.organization_detail?.name && ` · ${p.organization_detail.name}`}
            </Typography>
          </Box>
          {p.host?.image && <Avatar variant="rounded" src={p.host.image} sx={{ width: 64, height: 64 }} />}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip variant="outlined" label={EMPLOYMENT[p.employment_type]} />
          <Chip variant="outlined" label={WORK_MODE[p.work_mode]} />
          {(p.location || p.organization_detail?.country) && <Chip variant="outlined" icon={<PlaceIcon />} label={p.location || p.organization_detail.country} />}
          {p.deadline && <Chip variant="outlined" icon={<DeadlineIcon />} color={left !== null && left <= 7 && left >= 0 ? 'warning' : 'default'}
            label={left !== null && left >= 0 ? `Apply by ${fmtDeadline(p.deadline)}` : `Deadline passed (${fmtDeadline(p.deadline)})`} />}
          {p.compensation && <Chip variant="outlined" label={p.compensation} />}
        </Stack>

        <Box display="flex" gap={1} mt={3} flexWrap="wrap" alignItems="center">
          {!p.is_open ? <Chip label="Closed" /> : p.can_manage ? null : p.in_host_lab ? <Chip label="Your lab's position" /> : p.applied ? (
            <Chip color="success" label="You applied" />
          ) : (
            <Button variant="contained" size="large" onClick={() => setApplyOpen(true)} sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.92)' } }}>
              Apply on Otable
            </Button>
          )}
          {p.apply_url && <Button href={p.apply_url} target="_blank" rel="noopener noreferrer">External application page</Button>}
          {p.contact_email && <Button href={`mailto:${encodeURIComponent(p.contact_email)}`}>Contact</Button>}
          <Box flex={1} />
          {p.can_manage && (
            <>
              <Button variant="outlined" onClick={() => setApplicantsOpen(true)}>
                {p.application_count} applicant{p.application_count === 1 ? '' : 's'}
              </Button>
              <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>Edit</Button>
              <ConfirmIconDelete label="Remove the position" onConfirm={remove} />
            </>
          )}
        </Box>

        {p.description && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <LabMarkdown>{p.description}</LabMarkdown>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 3 }}>
          Posted {new Date(p.created).toLocaleDateString()}{p.posted_by_detail ? ` by ${p.posted_by_detail.name}` : ''}
        </Typography>
      </Paper>

      <ApplyDialog open={applyOpen} onClose={() => setApplyOpen(false)} position={p} onApplied={(data) => mutate(data, { revalidate: false })}
        applyUrl={`/consortium/positions/${p.id}/apply/`} hostName={p.host?.name} recipient={recipient} profileCv={p.viewer_has_profile_cv} />
      <ApplicantsDialog open={applicantsOpen} onClose={() => { setApplicantsOpen(false); mutate(); }} position={p}
        base={`/consortium/positions/${p.id}/applications`} />
      <PositionDialog open={editOpen} onClose={() => setEditOpen(false)} position={p} onSaved={(data) => mutate(data, { revalidate: false })} />
    </Container>
  );
}
